import * as React from 'react'
import { Link } from "react-router-dom"
import { Box, Card, CardActions, CardContent, CardHeader, Table, TableBody, TableRow, TableCell, Typography, Checkbox, IconButton, Menu, MenuItem, Divider, Alert } from '@mui/material'
import { tableCellClasses } from '@mui/material/TableCell'
import { MoreVert as MoreVertIcon } from '@mui/icons-material'
import { styled } from '@mui/system'
import { Masonry } from '@mui/lab'
import ConfirmDialog from 'components/ConfirmDialog'
import { OrdersContext } from 'contexts/ordersContext'
import { formatAddress } from 'utils/formatters'

// Data row styling
const DataRow = styled(TableRow)(({ theme }) => ({
  [`& td`]: {
    borderBottom: 0,
  },
  [`&:not(:first-of-type) td`]: {
    borderTop: '1px solid ' + theme.palette.divider,
  },
  [`&.wrap td, &.custom_wrap td`]: {
    color: '#7B3F00',
  },
  [`&.salad td, &.custom_salad td`]: {
    color: theme.palette.success.main,
  },
  [`&.breakfast td, &.custom_breakfast td`]: {
    color: theme.palette.warning.main,
  },
  [`&.side_salad td`]: {
    color: theme.palette.warning.main,
  },
  [`&.snack td`]: {
    color: theme.palette.info.main,
  },
  [`&.custom_meal td, &.custom_wrap td, &.custom_salad td, &.custom_breakfast td`]: {
    fontWeight: theme.typography.fontWeightRegular,
  },
}))

// Data cell styling
const DataCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.body}`]: {
    fontWeight: theme.typography.fontWeightMedium,
    verticalAlign: 'top',
    padding: '4px',
  },
  [`&.checkbox`]: {
    width: '32px',
    padding: '3px 4px',
  },
  [`&.quantity`]: {
    textAlign: 'right',
    textWrap: 'nowrap',
    width: '30px',
  },
}))

/**
 * OrderItem component
 */
const OrderItem = ({ item, updateItemStatus }) => {
  return (
    <DataRow className={item.type} hover selected={item.completed}>
      <DataCell className="checkbox">
        <Checkbox disableRipple checked={item.completed} onChange={(e) => updateItemStatus(e.target.checked)} sx={{ p: 0 }} />
      </DataCell>
      <DataCell className="quantity">{item.quantity} x</DataCell>
      <DataCell>
        {item.type === 'custom_meal' && item.description.length ? item.description : item.name}
        {item.notes && (
          <Typography variant="body2" color="error" sx={{ fontWeight: 'fontWeightBold' }}>
            {item.notes}
          </Typography>
        )}
      </DataCell>
    </DataRow>
  )
}

/**
 * Order component
 */
const Order = ({ order, deleteOrder }) => {
  const { updateOrderStatus, updateItemStatus } = React.useContext(OrdersContext)
  const [menuAnchorEl, setMenuAnchorEl] = React.useState(null)

  const handleMenuOpen = (e) => {
    setMenuAnchorEl(e.currentTarget)
  }

  const handleMenuClose = () => {
    setMenuAnchorEl(null)
  }

  const handleDeleteAction = (e) => {
    handleMenuClose()
    deleteOrder()
  }

  // Sorting items
  order.items.sort((a, b) => a.ordering - b.ordering)

  return (
    <Card variant="outlined">
      <CardHeader
        avatar={<Checkbox disableRipple checked={order.completed} onChange={(e) => updateOrderStatus(order._id, e.target.checked)} sx={{ padding: '0 0 0 4px' }} />}
        title={order.orderNo + ' - ' + order.customerName + ' (' + order.mealsNo + ')'}
        slotProps={{
          avatar: { sx: { marginRight: '.75rem' } },
          title: { style: { fontSize: '1rem' }, color: 'text.primary' },
          action: { sx: { alignSelf: 'center' } },
        }}
        sx={{ bgcolor: order.type, py: 2 }}
        action={
          <>
            <IconButton onClick={handleMenuOpen}>
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={menuAnchorEl}
              open={Boolean(menuAnchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              slotProps={{
                paper: { sx: { minWidth: 150 } },
                list: { component: 'div' }
              }}
            >
              {order.source === 'app' && (
                <MenuItem component={Link} to={`/orders/edit/${order._id}`}>Edit</MenuItem>
              )}
              <MenuItem component={Link} to={`/orders/create?from=${order._id}`}>Duplicate</MenuItem>
              {order.source === 'app' && [
                <Divider key="menu-divider" />,
                <MenuItem key="menu-delete" component={Link} onClick={handleDeleteAction} sx={{ color: 'error.main' }}>Delete</MenuItem>
              ]}
            </Menu>
          </>
        }
      />
      <CardContent>
        <Table size="small">
          <TableBody>
            {order.items.map((item) => (
              <OrderItem key={item._id} item={item} updateItemStatus={(status) => updateItemStatus(order._id, item._id, status)} />
            ))}
          </TableBody>
        </Table>
      </CardContent>
      {order.type == 'delivery' && order.deliveryAddress && (
        <CardActions sx={{ bgcolor: 'grey.200', p: 2 }}>
          <Typography variant="body2" component="div">
            {formatAddress(order.deliveryAddress).map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </Typography>
        </CardActions>
      )}
    </Card>
  )
}

/**
 * OrderGrid component
 */
const OrderGrid = ({ orders, noOrderMessage }) => {
  const { deleteOrder } = React.useContext(OrdersContext)
  const [orderToDelete, setOrderToDelete] = React.useState(null)
  const [confirmOpen, setConfirmOpen] = React.useState(false)

  const handleDeleteAction = (order) => {
    setOrderToDelete(order)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = () => {
    if (orderToDelete) deleteOrder(orderToDelete._id)
    setConfirmOpen(false)
  }

  // Sorting orders by customer name
  orders.sort((a, b) => a.customerName.localeCompare(b.customerName))

  return (
    <>
      {orders.length > 0 ? (
        <Masonry columns={{ xs: 1, sm: 2, md: 3, lg: 4 }} spacing={3} sx={{ width: 'auto' }}>
          {orders.map((order) => (
            <Order key={order._id} order={order} deleteOrder={() => handleDeleteAction(order)} />
          ))}
        </Masonry>
      ) : (
        <Alert severity="warning" sx={{ mb: 6, border: 1, borderColor: 'warning.light' }}>
          {noOrderMessage}
        </Alert>
      )}

      <ConfirmDialog
        open={confirmOpen}
        message={orderToDelete ? `Are you sure you want to delete order ${orderToDelete.orderNo}?` : ''}
        onConfirm={handleConfirmDelete}
        onClose={() => setConfirmOpen(false)}
      />
    </>
  )
}

export default OrderGrid
